import { ReactElement, useEffect, useState } from 'react';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import { MISSED_DAY_SCORE_SOURCES } from '../../types/MissedDayAllowance';

import type { MissedDayAllowance } from '../../types/MissedDayAllowance';

interface MissedDayAllowanceEditorProps {
  allowance?: MissedDayAllowance;
  isDQForEntireEvent?: boolean;
  /** How many days the combined total spans — a unit can miss at most one fewer. */
  dayCount: number;
  onAllowanceChange: (allowance: MissedDayAllowance | undefined) => void;
  onDQForEntireEventChange: (isDQForEntireEvent: boolean) => void;
}

// A combined total's missed-day settings (see MissedDayAllowance), the same in both places a total
// across days is set up: a composite game, and a game totalling its own days.
export default function MissedDayAllowanceEditor({
  allowance,
  isDQForEntireEvent,
  dayCount,
  onAllowanceChange,
  onDQForEntireEventChange,
}: MissedDayAllowanceEditorProps): ReactElement {
  // A string so the field can be blank mid-edit instead of snapping to 0.
  const [fixedScoreText, setFixedScoreText] = useState(String(allowance?.fixedScore ?? 0));
  useEffect(() => {
    setFixedScoreText(String(allowance?.fixedScore ?? 0));
  }, [allowance?.fixedScore]);

  const maxOptions = Array.from({ length: Math.max(1, dayCount - 1) }, (_, i) => i + 1);

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">Days a player can miss</Typography>
        <ToggleButtonGroup
          exclusive size="small"
          value={allowance ? String(allowance.maxMissedDays) : 'off'}
          onChange={(_, val: string | null) => {
            if (val === null) return;
            onAllowanceChange(val === 'off' ? undefined : {
              scoreSource: MISSED_DAY_SCORE_SOURCES.PLAYED_AVERAGE,
              ...allowance,
              maxMissedDays: Number(val),
            });
          }}
        >
          <ToggleButton value="off">None</ToggleButton>
          {maxOptions.map((n) => (
            <ToggleButton key={n} value={String(n)}>{n}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

      {allowance && (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">A missed day counts as</Typography>
          <ToggleButtonGroup
            exclusive size="small"
            value={allowance.scoreSource}
            onChange={(_, val) => {
              if (val === null) return;
              onAllowanceChange({ ...allowance, scoreSource: val });
            }}
          >
            <ToggleButton value={MISSED_DAY_SCORE_SOURCES.PLAYED_AVERAGE}>Their average</ToggleButton>
            <ToggleButton value={MISSED_DAY_SCORE_SOURCES.FIXED}>Fixed score</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary">
            {allowance.scoreSource === MISSED_DAY_SCORE_SOURCES.FIXED
              ? 'Each missed game counts as this score.'
              : 'Each missed game counts as their average over the games they played, scaled to its holes (and its par, for strokes), rounded to a whole score.'}
            {' '}A day a partner misses counts as missed; missing more days than this is DNF.
          </Typography>
        </Stack>
      )}

      {allowance?.scoreSource === MISSED_DAY_SCORE_SOURCES.FIXED && (
        <TextField
          label="Score for each missed game"
          size="small"
          type="number"
          value={fixedScoreText}
          slotProps={{ htmlInput: { step: 1 } }}
          sx={{ maxWidth: '14rem' }}
          onChange={({ target }) => setFixedScoreText(target.value)}
          onBlur={() => {
            const fixedScore = parseFloat(fixedScoreText);
            if (isFinite(fixedScore) && fixedScore !== allowance.fixedScore) onAllowanceChange({ ...allowance, fixedScore });
          }}
        />
      )}

      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">Disqualified on any day</Typography>
        <ToggleButtonGroup
          exclusive size="small"
          value={isDQForEntireEvent === false ? 'missed' : 'out'}
          onChange={(_, val) => {
            if (val === null) return;
            onDQForEntireEventChange(val === 'out');
          }}
        >
          <ToggleButton value="out">Out of the total</ToggleButton>
          <ToggleButton value="missed">Counts as a missed day</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
    </Stack>
  );
}
